import { Button, FlagFilledSVG, RefreshSVG, useModal } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import ReportContentFormModal from "@/view/ReportContentFormModal";
import useViewPageContentContext from "@/view/ViewPageContent/useViewPageContentContext";
import {
  errorButtonCss,
  errorButtonWrapperCss,
  errorTextCss,
} from "./ListenToTranslationError.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface ListenToTranslationErrorProps {
  allowTryAgain: boolean;
  languageCode: string;
  onTryAgain?: () => void;
}

const ListenToTranslationError = ({
  allowTryAgain,
  languageCode,
  onTryAgain,
}: ListenToTranslationErrorProps) => {
  const { content } = useViewPageContentContext();
  const [reportContentModalProps, setReportContentModalActive] =
    useModal(false);

  const SvgComponent = allowTryAgain ? RefreshSVG : FlagFilledSVG;
  const buttonText = allowTryAgain ? "Try again" : "Report error";
  const message = allowTryAgain
    ? "Sorry, there was a problem loading the translation audio."
    : "Sorry, we can't play this audio at the moment.";

  // Event tracking variables
  const actionName = allowTryAgain ? actionNames.interact : actionNames.start;
  const objectType = allowTryAgain
    ? objectTypes.translation
    : objectTypes.report;
  const eventDescription = allowTryAgain
    ? `Try again to generate audio file for the translated text in language ${languageCode}`
    : `Report error for the translated audio in language ${languageCode}`;
  const qaSelector = allowTryAgain
    ? `retry-generating-audio-file-for-translation`
    : `report-translation-audio-error`;

  const handleClick = () => {
    if (allowTryAgain) {
      onTryAgain?.();
    } else {
      setReportContentModalActive(true);
    }
  };

  return (
    <>
      <p css={errorTextCss}>{message}</p>
      <div css={errorButtonWrapperCss}>
        <ButtonWithTrackEvent
          __cssFor={{ root: errorButtonCss }}
          icon={{ alignment: "right", SvgComponent }}
          onClick={handleClick}
          flavor={Button.flavors.incognito}
          actionName={actionName}
          objectType={objectType}
          streamProperties={{ description: eventDescription }}
          dataProps={{ "data-qa-selector": qaSelector }}
        >
          {buttonText}
        </ButtonWithTrackEvent>
        <ReportContentFormModal
          contentId={content?.id || ""}
          reportContentModalProps={reportContentModalProps}
          setReportContentModalActive={setReportContentModalActive}
          reportSource="Translate"
        />
      </div>
    </>
  );
};

export default ListenToTranslationError;
